/**
 * Text Cell Editor - Rich text editing with formatting support
 */

import React, { useRef, useEffect, useCallback } from "react";
import { BubbleMenu } from "@/components/editor/BubbleMenu";
import { useSelectionManager } from "@/components/editor/hooks/useSelectionManager";
import { applyFormatting, processHTMLContent } from "@/lib/utils/textFormatting";
import { sanitizeBasicHtml, escapeHtml } from "@/lib/utils/pasteHandler";
import CellEditorWrapper from "./CellEditorWrapper";
import { TextCellEditorProps } from "./types";

const CellEditorText: React.FC<TextCellEditorProps> = React.memo(({
  value,
  onChange,
  onSave,
  onCancel,
  isVisible,
  cellRect,
  cellWidth,
  placeholder = "Enter text...",
}) => {
  const isCancellingRef = useRef(false);
  const contentChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const initialValueRef = useRef(value);
  const hasInitializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  
  // Keep onChange ref up to date
  onChangeRef.current = onChange;

  // Text selection and bubble menu management
  const { bubbleMenu, selectedText, hideBubbleMenu } = useSelectionManager(editorRef);

  // Capture initial value when editor opens
  useEffect(() => {
    if (isVisible && !hasInitializedRef.current) {
      initialValueRef.current = value;
      hasInitializedRef.current = true;
      if (editorRef.current) {
        editorRef.current.innerHTML = value;
      }
    }
    if (!isVisible) {
      hasInitializedRef.current = false;
    }
  }, [isVisible]); // Note: intentionally NOT including 'value' to prevent re-initialization

  // Focus management when editor becomes visible
  useEffect(() => {
    if (isVisible && editorRef.current) {
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }, 50);
    }
  }, [isVisible]);

  // Save and restore cursor position
  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(editorRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      return preCaretRange.toString().length;
    }
    return 0;
  }, []);

  const restoreCursorPosition = useCallback((position: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT,
      null
    );

    let currentPosition = 0;
    let node = walker.nextNode();

    while (node) {
      const nodeLength = node.textContent?.length || 0;
      if (currentPosition + nodeLength >= position) {
        const range = document.createRange();
        range.setStart(node, position - currentPosition);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      currentPosition += nodeLength;
      node = walker.nextNode();
    }
  }, []);

  // Debounced content change
  const debouncedContentChange = useCallback(() => {
    if (editorRef.current && !isCancellingRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      const processedContent = processHTMLContent(htmlContent);
      onChangeRef.current(processedContent);
    }
  }, []);

  // Handle content change
  const handleContentChange = useCallback(() => {
    if (contentChangeTimeoutRef.current) {
      clearTimeout(contentChangeTimeoutRef.current);
    }
    contentChangeTimeoutRef.current = setTimeout(debouncedContentChange, 150);
  }, [debouncedContentChange]);

  // Handle formatting commands from bubble menu
  const handleFormat = useCallback(
    (command: string, formatValue?: string) => {
      if (editorRef.current) {
        const cursorPosition = saveCursorPosition();
        const formattedContent = applyFormatting(command, formatValue, editorRef.current);
        onChangeRef.current(formattedContent);
        setTimeout(() => {
          restoreCursorPosition(cursorPosition);
        }, 0);
        hideBubbleMenu();
      }
    },
    [hideBubbleMenu, saveCursorPosition, restoreCursorPosition]
  );

  // Handle paste
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();

      const html = e.clipboardData.getData("text/html");
      const plainText = e.clipboardData.getData("text/plain");

      let contentToInsert = html ? sanitizeBasicHtml(html) : escapeHtml(plainText);

      contentToInsert = contentToInsert
        .replace(/\r\n/g, "<br>")
        .replace(/\r/g, "<br>")
        .replace(/\n/g, "<br>")
        .replace(/(<br\s*\/?>){3,}/g, "<br><br>");

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();

        const div = document.createElement("div");
        div.innerHTML = contentToInsert;
        const fragment = document.createDocumentFragment();
        while (div.firstChild) {
          fragment.appendChild(div.firstChild);
        }

        range.insertNode(fragment);
        handleContentChange();
      }
    },
    [handleContentChange]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    isCancellingRef.current = true;
    if (editorRef.current) {
      editorRef.current.innerHTML = initialValueRef.current;
    }
    onCancel();
    setTimeout(() => {
      isCancellingRef.current = false;
    }, 100);
  }, [onCancel]);

  // Handle key events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" && e.ctrlKey) {
        e.preventDefault();
        onSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancel();
      }
    },
    [onSave, handleCancel]
  );

  // Cleanup
  useEffect(() => {
    return () => {
      if (contentChangeTimeoutRef.current) {
        clearTimeout(contentChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <CellEditorWrapper
        isVisible={isVisible}
        cellRect={cellRect}
        cellWidth={cellWidth}
        onSave={onSave}
        onCancel={handleCancel}
        minHeight={80}
      >
        <div
          ref={editorRef}
          contentEditable
          dir="auto"
          suppressContentEditableWarning={true}
          onInput={handleContentChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-placeholder={placeholder}
          className="w-full h-full border-none outline-none bg-transparent focus:outline-none p-2 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none text-foreground"
          style={{
            lineHeight: "1.5",
            fontSize: "14px",
            fontFamily: "inherit",
            wordWrap: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        />
      </CellEditorWrapper>

      {/* BubbleMenu for text formatting */}
      {isVisible && (
        <BubbleMenu
          isVisible={bubbleMenu.isVisible}
          position={bubbleMenu.position}
          onFormat={handleFormat}
          selectedText={selectedText}
        />
      )}
    </>
  );
}, (prevProps, nextProps) => {  
  // Only re-render if these props change (ignore value changes while editing)
  return prevProps.isVisible === nextProps.isVisible &&
    prevProps.cellRect === nextProps.cellRect &&
    prevProps.cellWidth === nextProps.cellWidth &&
    prevProps.onSave === nextProps.onSave &&
    prevProps.onCancel === nextProps.onCancel &&
    prevProps.placeholder === nextProps.placeholder;
});

CellEditorText.displayName = 'CellEditorText';
export default CellEditorText;
